import React, { useMemo } from 'react'
import { Box, Typography, Chip, Stack, ButtonBase, Alert } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

interface Slot {
  date: string
  startTime: string
  endTime: string
}

interface CurrentBooking {
  date: string
  startTime: string
  meetingMode?: string
}

interface SlotPickerProps {
  slots: Slot[]
  selectedSlot: Slot | null
  currentBooking: CurrentBooking | null
  onSelect: (slot: Slot) => void
  disabled: boolean
  timezoneAbbr?: string
}

const SlotPicker: React.FC<SlotPickerProps> = ({
  slots,
  selectedSlot,
  currentBooking,
  onSelect,
  disabled,
  timezoneAbbr = 'ET'
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, Slot[]>()
    for (const slot of slots) {
      const existing = map.get(slot.date) ?? []
      existing.push(slot)
      map.set(slot.date, existing)
    }
    return map
  }, [slots])

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  }

  const formatDay = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.getDate()
  }

  const formatDayName = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
  }

  const formatTime = (timeStr: string) => {
    const [h, m] = timeStr.split(':')
    const hour = Number.parseInt(h, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    let displayHour = hour % 12
    if (displayHour === 0) displayHour = 12
    return `${displayHour}:${m} ${period} ${timezoneAbbr}`
  }

  if (slots.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2, borderRadius: '11px' }}>
        No available slots this month. Reply to the email and we&apos;ll find a time.
      </Alert>
    )
  }

  return (
    <Stack spacing={2.75}>
      {[...grouped.entries()].map(([date, dateSlots]) => {
        const isDateSelected = selectedSlot?.date === date
        return (
          <Box key={date}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 1.5 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '11px',
                  bgcolor: isDateSelected ? '#007680' : '#EEEBE2',
                  border: '1px solid',
                  borderColor: isDateSelected ? '#007680' : '#E4E0D5',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    letterSpacing: 0.5,
                    color: isDateSelected ? 'rgba(255,255,255,0.75)' : '#8A9295'
                  }}
                >
                  {formatDayName(date)}
                </Typography>
                <Typography
                  sx={{ fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.2, color: isDateSelected ? '#fff' : '#002A3A' }}
                >
                  {formatDay(date)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#002A3A', lineHeight: 1.2 }}>
                  {formatDate(date)}
                </Typography>
                <Typography sx={{ fontWeight: 300, fontSize: '0.8rem', color: '#9AA0A0', mt: 0.25 }}>
                  {dateSlots.length} slot{dateSlots.length > 1 ? 's' : ''} available
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {dateSlots.map((slot) => {
                const isSelected = selectedSlot?.date === slot.date && selectedSlot?.startTime === slot.startTime
                const isCurrent = currentBooking?.date === slot.date && currentBooking?.startTime === slot.startTime

                let borderColor = '#E3E0D8'
                let bgColor = '#FBFAF7'
                let iconColor = '#B7BDBD'
                let boxShadow = 'none'
                if (isSelected) {
                  borderColor = '#007680'
                  bgColor = 'rgba(0,118,128,0.09)'
                  iconColor = '#007680'
                  boxShadow = '0 0 0 1px #007680 inset'
                } else if (isCurrent) {
                  borderColor = '#B49D77'
                  bgColor = '#FBF3E6'
                  iconColor = '#B49D77'
                }

                return (
                  <ButtonBase
                    key={`${slot.date}-${slot.startTime}`}
                    onClick={() => !disabled && onSelect(slot)}
                    disabled={disabled}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                      py: 1.75,
                      px: 1,
                      borderRadius: '11px',
                      border: '1.5px solid',
                      borderColor,
                      bgcolor: bgColor,
                      boxShadow,
                      opacity: disabled ? 0.5 : 1,
                      transition: 'all 0.15s ease',
                      '&:hover': disabled
                        ? {}
                        : { borderColor: '#007680', bgcolor: 'rgba(0,118,128,0.09)' }
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 16, color: iconColor }} />
                    <Typography
                      sx={{ fontWeight: 600, fontSize: '0.92rem', color: '#002A3A', lineHeight: 1.3 }}
                    >
                      {formatTime(slot.startTime)}
                    </Typography>
                    {isCurrent && (
                      <Chip
                        label="Current"
                        size="small"
                        sx={{ mt: 0.25, fontSize: '0.58rem', height: 18, bgcolor: '#B49D77', color: '#fff' }}
                      />
                    )}
                  </ButtonBase>
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Stack>
  )
}

export default SlotPicker
